import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';

// The bar's fill is the surface it is over rather than one fixed colour: black over a
// dark photo is a shadow the links sit on, but black over the CS landing's yellow is a
// bruise across the top of the page. A section declares what the bar should carry while
// it is crossing it with data-header-fill="r, g, b". Anything that declares nothing keeps
// the black, which is what the two portfolio runs want: their ground is black already.
const DEFAULT_FILL = [0, 0, 0];

// The CS landing's yellow — see pages/cs/CSHeroSection, which it is taken from. It is also
// the accent the bar carries everywhere else, so on this field the links would be painting
// the ground onto itself; they swap to the landing's own blue for as long as the fill under
// them is this yellow. (The blue is written out literally where it is used, since Tailwind
// can't see a class name assembled at runtime.)
const CS_FIELD = '247, 219, 0';

const LIFT_PX = 90;   // distance over which the backdrop fades up from nothing

const BAR_PX = 72;    // roughly the bar's own height, for the surface measurement below

// How deep a band of page the fill is averaged over, so a section boundary passing
// through takes this many pixels of scrolling to hand the colour over instead of
// switching it on one frame. It can't be left to a CSS transition: the fill lives in a
// gradient's colour stops, which don't interpolate, so the mixing happens per frame and
// is tied to scroll position — which also lets it scrub backwards.
const BLEND_PX = 220;

const clamp = (n) => Math.min(1, Math.max(0, n));

// The declared sections crossing the blend band, each with how much of the band it covers.
// They're assumed not to overlap — the page is a stack of sections — so the covered
// lengths simply sum, and whatever is left over is page that declared nothing.
const bandCoverage = (lo, hi) =>
  Array.from(document.querySelectorAll('[data-header-fill]'))
    .map((el) => {
      const { top, bottom } = el.getBoundingClientRect();
      return {
        fill: el.dataset.headerFill,
        overlap: Math.max(0, Math.min(hi, bottom) - Math.max(lo, top)),
      };
    })
    .filter((zone) => zone.overlap > 0);

export default function Header() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // How far the bar has lifted off the top of the page (0–1). A fraction rather than a
  // boolean because a CSS transition cannot interpolate a gradient — the alpha has to be
  // mixed here, per frame, or the change is a jump.
  const [lift, setLift] = useState(0);

  // What the bar is currently over. Measured off the sections themselves rather than off
  // scroll offsets, so it stays right however long each one's scroll track is.
  const [fill, setFill] = useState(DEFAULT_FILL.join(', '));

  // How much of the band under the bar is the CS landing's yellow (0–1). Kept apart from
  // the fill because what it drives — the link colour — is a Tailwind class, which can
  // only be swapped whole rather than mixed. See the accent below.
  const [fieldShare, setFieldShare] = useState(0);

  useEffect(() => {
    const update = () => {
      // Rounded to cut re-renders that change nothing.
      setLift(Math.round(clamp(window.scrollY / LIFT_PX) * 100) / 100);

      // Weighting each section by the overlap itself, rather than sampling at a few fixed
      // depths, keeps a boundary sweeping through the band a smooth ramp instead of a
      // short flight of steps.
      const lo = BAR_PX - BLEND_PX / 2;
      const hi = BAR_PX + BLEND_PX / 2;
      const zones = bandCoverage(lo, hi);

      let field = 0;
      const mixed = zones.reduce(
        (acc, zone) => {
          const weight = zone.overlap / BLEND_PX;
          if (zone.fill === CS_FIELD) field += weight;
          zone.fill.split(',').forEach((channel, i) => {
            acc[i] += Number(channel) * weight;
          });
          return acc;
        },
        // Seeded with the whole band's worth of the default, and each declared section's
        // share taken back off it again — so page that declares nothing needs no separate
        // pass, and a page with no declarations at all lands on plain black.
        [...DEFAULT_FILL]
      );
      const declared = zones.reduce((sum, zone) => sum + zone.overlap, 0) / BLEND_PX;
      DEFAULT_FILL.forEach((channel, i) => {
        mixed[i] -= channel * declared;
      });

      setFill(mixed.map(Math.round).join(', '));
      setFieldShare(field);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [location.pathname]);

  // The bar carries whatever accent the route under it is set in, falling back to the
  // site's yellow — the same one the CS landing is printed on, which the home page's links
  // and copy are already set in, so the bar matches them the whole way down. The art
  // portfolio's landing photo runs full-bleed behind the bar with no scrim, and the yellow
  // doesn't sit on it cleanly, so that route swaps to the red its title carries. The CS
  // landing swaps mid-page rather than per route, to the blue its own type is set in — that
  // one is decided by the share of the band the yellow holds rather than by the fill itself,
  // which is a blend and only lands exactly on the yellow in the middle of the landing; it
  // tips at the halfway mark, where the ground has stopped being one colour and started
  // being the other. Order matters: the field test has to beat the route test, since the
  // yellow bar would vanish into the CS landing's yellow ground.
  const isArtPortfolio = location.pathname === '/art-portfolio';
  const overField = fieldShare > 0.5;
  const accent = isArtPortfolio
    ? 'text-[#E11D2E]'
    : overField
      ? 'text-[#1519D8]'
      : 'text-[#F7DB00]';
  // Every hover is a lighter tint of the accent the link already carries rather than a
  // jump to white — on these fields white was a harder change than the rest of the site's
  // yellow → light-yellow, and read as a different link rather than the same one lit.
  const accentHover = isArtPortfolio
    ? 'hover:text-[#F2707A]'
    : overField
      ? 'hover:text-[#5C5FEA]'
      : 'hover:text-[#FFEB5C]';
  const accentBorder = isArtPortfolio
    ? 'border-[#E11D2E]'
    : overField
      ? 'border-[#1519D8]'
      : 'border-[#F7DB00]';
  const accentActiveBg = isArtPortfolio
    ? 'bg-[#E11D2E]/15'
    : overField
      ? 'bg-[#1519D8]/15'
      : 'bg-[#F7DB00]/15';

  // Transparent while the bar is still sitting over the very top of the page, so a landing
  // photo runs full-bleed up behind it, then fading up to full as the page lifts away.
  const rgba = (alpha) => `rgba(${fill}, ${alpha * lift})`;
  // A gradient rather than a filled bar so there's no hard bottom edge, but near enough
  // to solid across the bar itself that the links are read against the fill rather than
  // against whatever is passing under it; the fade is what keeps it from being a box. The
  // last stop is the same colour at zero alpha rather than `transparent`, which some
  // engines interpolate through grey and leave a dirty band mid-fade.
  const headerGradient =
    `linear-gradient(to bottom, ${rgba(0.92)}, ${rgba(0.6)} 50%, ${rgba(0)})`;

  // With the mobile menu open the bar and the panel under it are one surface, so the fill
  // is painted once across the whole header: solid down to the foot of the panel, washing
  // out over the pb-16 tail below it, so the open menu reads as the bar having grown
  // rather than as a box stuck to it. At full strength rather than through `lift`, since
  // the menu can be opened at the top of the page where the bar is still transparent.
  const solid = (alpha) => `rgba(${fill}, ${alpha})`;
  const menuGradient =
    `linear-gradient(to bottom, ${solid(0.97)}, ${solid(0.95)} calc(100% - 4rem), ${solid(0)})`;

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/art-portfolio', label: 'Art Portfolio' },
    { path: '/cs-portfolio', label: 'CS Portfolio' }
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // pointer-events-none on the wrapper so the tall, mostly-transparent fade zone
  // doesn't swallow clicks meant for the page; the bar and menu opt back in.
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none pb-16"
      style={{ backgroundImage: headerGradient }}
    >
      {/* The open menu's fill, as its own layer over the bar's: opacity is the one thing
          that can be transitioned here — a gradient's own colour stops cannot — so fading
          this keeps the fill in step with the panel sliding open. First child, so it
          paints under the bar and the links without needing a z-index. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 md:hidden transition-opacity duration-300 ease-in-out ${
          isMenuOpen ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundImage: menuGradient }}
      />

      {/* The wide desktop margins are held back to lg — at phone widths they left the
          logo and the hamburger squeezed together in the middle of the bar instead of
          sitting on its two edges. */}
      <div className="relative pointer-events-auto px-5 md:px-10 lg:px-0 lg:mx-28 lg:mr-[10rem] py-4 flex items-center justify-between">
        <Link
          to="/"
          className={`${accent} ${accentHover} font-bold text-3xl sm:text-4xl transition-colors`}
          style={{ fontFamily: '"Nanum Pen Script", cursive' }}
          onClick={closeMenu}
        >
          <span style={{ fontFamily: '"Zaslia", cursive' }}>C.</span>
          <span style={{ fontFamily: '"Priestacy", cursive' }}>B</span>
        </Link>
        
        <nav className="hidden md:flex space-x-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-lg font-light transition-colors ${accent} ${accentHover} ${
                location.pathname === item.path ? `border-b-2 ${accentBorder}` : ''
              }`}
              style={{ fontFamily: '"Zaslia", serif' }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <button
          onClick={toggleMenu}
          // -mr-2 cancels the tap padding on the outer side only, so the icon lines up
          // on the same margin as the logo rather than sitting 8px short of it.
          className={`md:hidden -mr-2 ${accent} ${accentHover} transition-colors p-2`}
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* No fill of its own — the layer above carries it across both. */}
      <div
        className={`relative pointer-events-auto md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
      >
        <nav className="px-5 py-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMenu}
              className={`block text-base font-light transition-colors py-2 px-3 rounded ${accent} ${accentHover} ${
                location.pathname === item.path ? accentActiveBg : ''
              }`}
              style={{ fontFamily: '"Zaslia", serif' }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}